/**
 * Regras de cupom — fonte única pro /cart (assets/cart-discount-orchestrator.js)
 * e pro carrinho da adição facilitada (assets/zrzs-quick-add-discount.js).
 *
 * Módulo (@theme/discount-coupon-rules no importmap de snippets/scripts.liquid):
 * quem usa importa direto.
 *
 * - Nativo (Shopify) = código que começa com um prefixo fixo abaixo ou com um
 *   prefixo configurável (settings.native_discount_coupon_prefixes). O resto
 *   vai pro DiscountYard.
 * - Com desconto por linha no carrinho, cupom não nativo só passa se começar
 *   com um prefixo cumulativo (settings.isca_cumulative_coupon_prefixes).
 * - Com cupom nativo ativo, cupom do DiscountYard não combina.
 */

/** Prefixos fixos de cupom nativo e o tipo de cada um (pra mensagem de "não combinável"). */
const FIXED_NATIVE_PREFIXES = [
  { prefix: 'TROCA', type: 'TROCA' },
  { prefix: 'RETIRADA', type: 'RETIRADA' },
  { prefix: 'UNIFORME', type: 'UNIFORME' },
  { prefix: '40MENSAL', type: '40MENSAL' },
  { prefix: 'ZRZS-DUPLA', type: 'ZRZS-DUPLA' },
  { prefix: 'MAIS', type: 'MAIS' },
  { prefix: 'TESTE-', type: 'TESTE' },
];

/** Bloqueados no ecommerce (igualdade, sem diferenciar maiúsculas). */
const BLOCKED_CODES = ['PROMODEZ25', 'PROMODEZ25-LENTES'];

/** Mensagens padrão (pt-BR). O quick add sobrescreve com as traduções do tema. */
export const DEFAULT_MESSAGES = {
  blocked: 'Desconto não permitido no ecommerce',
  notCumulative:
    'Este cupom não é cumulativo com outros descontos já existentes no seu carrinho.',
  notCombinable: {
    TROCA: 'Cupons de troca não podem ser combinados com outros descontos.',
    RETIRADA: 'Cupons de retirada não podem ser combinados com outros descontos.',
    UNIFORME: 'Cupons de uniforme não podem ser combinados com outros descontos.',
    '40MENSAL': 'Cupons 40MENSAL não podem ser combinados com outros descontos.',
    'ZRZS-DUPLA': 'Cupons ZRZS-DUPLA não podem ser combinados com outros descontos.',
    MAIS: 'Cupons MAIS não podem ser combinados com outros descontos.',
    TESTE: 'Cupons de teste não podem ser combinados com outros descontos.',
    CUSTOM: 'Este cupom não pode ser combinado com outros descontos.',
  },
};

/**
 * "sol10\nEXTRA10, X" → ['SOL10', 'EXTRA10', 'X'].
 * @param {string} raw
 * @returns {string[]}
 */
export function parsePrefixList(raw) {
  if (!raw || !raw.trim()) return [];
  return raw
    .replace(/,/g, '||')
    .split(/\|\||\r?\n/)
    .map(prefix => prefix.trim().toUpperCase())
    .filter(Boolean);
}

/**
 * @param {string} code
 * @param {string[]} prefixes já em maiúsculas (parsePrefixList)
 * @returns {boolean}
 */
export function startsWithAny(code, prefixes) {
  const codeUpper = String(code || '').trim().toUpperCase();
  return prefixes.some(prefix => codeUpper.startsWith(prefix));
}

/**
 * Tipo do cupom nativo, ou null se não for nativo.
 * @param {string} code
 * @param {string[]} nativePrefixes prefixos configuráveis (parsePrefixList)
 * @returns {string | null} TROCA | RETIRADA | UNIFORME | 40MENSAL | ZRZS-DUPLA | MAIS | TESTE | CUSTOM
 */
export function getNativeDiscountType(code, nativePrefixes) {
  const codeUpper = String(code || '').trim().toUpperCase();
  for (let i = 0; i < FIXED_NATIVE_PREFIXES.length; i += 1) {
    if (codeUpper.startsWith(FIXED_NATIVE_PREFIXES[i].prefix)) {
      return FIXED_NATIVE_PREFIXES[i].type;
    }
  }
  return startsWithAny(codeUpper, nativePrefixes) ? 'CUSTOM' : null;
}

/**
 * @param {string} code
 * @param {string[]} nativePrefixes
 */
export function isNativeDiscountCode(code, nativePrefixes) {
  return getNativeDiscountType(code, nativePrefixes) !== null;
}

/** @param {string} code */
export function isBlockedCode(code) {
  return BLOCKED_CODES.indexOf(String(code || '').trim().toUpperCase()) !== -1;
}

/**
 * Isca cumulativa: com desconto por linha, cupom não nativo precisa de prefixo cumulativo.
 * @param {string} code
 * @param {{ nativePrefixes: string[], iscaPrefixes: string[], hasLineLevelDiscounts: boolean }} context
 */
export function passesIscaCumulativeRule(code, context) {
  if (!context.hasLineLevelDiscounts) return true;
  if (isNativeDiscountCode(code, context.nativePrefixes)) return true;
  return startsWithAny(code, context.iscaPrefixes);
}

/**
 * @param {string} type tipo de getNativeDiscountType
 * @param {typeof DEFAULT_MESSAGES} [messages]
 */
export function notCombinableMessage(type, messages) {
  const table = (messages || DEFAULT_MESSAGES).notCombinable;
  return table[type] || table.CUSTOM;
}

/**
 * Validação completa antes de enviar, na ordem do /cart: bloqueado →
 * isca cumulativa → nativo ativo + cupom do DiscountYard.
 * @param {string} code
 * @param {{ nativePrefixes: string[], iscaPrefixes: string[], hasLineLevelDiscounts: boolean, activeNativeCodes: string[] }} context
 * @param {typeof DEFAULT_MESSAGES} [messages]
 * @returns {{ ok: true, route: 'native' | 'discountyard' } | { ok: false, message: string }}
 */
export function validateCoupon(code, context, messages) {
  const texts = messages || DEFAULT_MESSAGES;
  if (isBlockedCode(code)) return { ok: false, message: texts.blocked };
  if (!passesIscaCumulativeRule(code, context)) {
    return { ok: false, message: texts.notCumulative };
  }

  const isNative = isNativeDiscountCode(code, context.nativePrefixes);
  let activeNativeType = null;
  for (let i = 0; i < context.activeNativeCodes.length; i += 1) {
    activeNativeType = getNativeDiscountType(context.activeNativeCodes[i], context.nativePrefixes);
    if (activeNativeType) break;
  }
  if (activeNativeType && !isNative) {
    return { ok: false, message: notCombinableMessage(activeNativeType, texts) };
  }

  return { ok: true, route: isNative ? 'native' : 'discountyard' };
}
